import React, { PropsWithChildren, useEffect, useState } from 'react';

import { Card, CardBody, CardFooter } from '../Card/Card';
import { IconClose } from '../Icons';

export type ModalProps = PropsWithChildren<{
    show: boolean;
    onEscapeKeyDown?: () => void;
}>;

export type ModalHeaderProps = PropsWithChildren<{
    onHide: () => void;
}>;

export const Modal = ({ show, onEscapeKeyDown, children }: ModalProps) => {
    const [shouldRender, setShouldRender] = useState(show);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (onEscapeKeyDown) {
                    onEscapeKeyDown();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onEscapeKeyDown]);

    useEffect(() => {
        if (show) {
            setShouldRender(true);
        } else {
            // This is to allow the modal to animate out before being removed from the DOM
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [show]);

    return (
        <div
            className={`z-[1000] bg-neutral-500 bg-opacity-50 transition-all duration-300 ease-in-out fixed w-full h-full inset-0 m-0 p-10 overflow-auto ${
                show ? 'opacity-100 pointer-events-auto ' : 'opacity-0 pointer-events-none delay'
            }`}
        >
            <div
                className={`max-w-[600px] mx-auto transform transition-all duration-300 ease-in-out ${
                    show ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
            >
                {shouldRender && <Card>{children}</Card>}
            </div>
        </div>
    );
};

const ModalHeader = ({ onHide, children }: ModalHeaderProps) => {
    return (
        <div className="px-4 md:px-8 pt-8 text-left">
            <div className="relative">
                {children}
                <div className="absolute top-0 right-0 cursor-pointer">
                    <IconClose height="24" width="24" onClick={onHide} />
                </div>
            </div>
        </div>
    );
};
Modal.Header = ModalHeader;

const ModalBody = ({ children }: PropsWithChildren) => {
    return <CardBody>{children}</CardBody>;
};
Modal.Body = ModalBody;

const ModalFooter = ({ children }: PropsWithChildren) => {
    return <CardFooter>{children}</CardFooter>;
};
Modal.Footer = ModalFooter;
